import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  List,
  Button,
  Image, Tag, Tooltip, Modal, Form, Input, Typography, Spin, Flex, Collapse, Switch, ConfigProvider
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLocations,
  openModal,
  deleteLocation, addLocation, resetAddStatus, clearCurrentLocation, fetchLocationById, patchLocation
} from '../../slices/locationSlice';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../Helper/NotificationProvider';
import placeholderSVG from '../../Icons/placeholder.svg'
import AddLocationModal from './AddLocationModal';
import { fetchEvents } from '../../slices/eventSlice';
import { deleteData, getData, postImage } from '../../service/APIClient';
import { URL_IMAGES } from '../../service/endpoints';

const { confirm } = Modal;
const { Text } = Typography;

const FittingTitle = ({ name }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.fontSize = '14px';
    while (el.scrollWidth > el.clientWidth && parseFloat(el.style.fontSize) > 8) {
      el.style.fontSize = (parseFloat(el.style.fontSize) - 0.5) + 'px';
    }
  }, [name]);
  return (
    <div ref={ref} title={name}
      style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', marginBottom: 0 }}>
      {name}
    </div>
  );
};

const Locations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const locations = useSelector(state => state.locations.data);
  const events = useSelector(state => state.events.data);
  const eventStatus = useSelector(state => state.events.status);
  const locationsStatus = useSelector(state => state.locations.status);
  const locationsError = useSelector(state => state.locations.error);

  const [expandedIds, setExpandedIds] = useState(new Set());
  const allExpanded = locations?.length > 0 && expandedIds.size === locations.length;

  const toggleAll = (on) => {
    setExpandedIds(on ? new Set(locations.map(l => l._id)) : new Set());
  };

  const toggleOne = (id, open) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      open ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const [reloadingIds, setReloadingIds] = useState(new Set());

  const handleReloadThumbnail = async (item) => {
    const id = item._id;
    setReloadingIds(prev => new Set(prev).add(id));
    try {
      const shapes = (item.layout || []).map(entry => entry.shape).filter(Boolean);
      if (shapes.length === 0) throw new Error('Es befinden sich keine Stühle in diesem Layout');

      // Render shapes to offscreen canvas (same logic as PixiEditorSimple.toBlob)
      const PADDING = 20;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const s of shapes) {
        if (s.x < minX) minX = s.x;
        if (s.y < minY) minY = s.y;
        if (s.x + s.width > maxX) maxX = s.x + s.width;
        if (s.y + s.height > maxY) maxY = s.y + s.height;
      }
      minX -= PADDING; minY -= PADDING; maxX += PADDING; maxY += PADDING;
      const contentW = maxX - minX;
      const contentH = maxY - minY;
      const THUMB_MAX = 1200;
      const scale = Math.min(1, THUMB_MAX / Math.max(contentW, contentH));
      const offscreen = document.createElement('canvas');
      offscreen.width = Math.round(contentW * scale);
      offscreen.height = Math.round(contentH * scale);
      const ctx = offscreen.getContext('2d');
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, offscreen.width, offscreen.height);
      // First markers (orange-highlighted chairs) come from the scan paths
      const scanPaths = item.scanPaths || [];
      const firstMarkerSet = new Set(
        scanPaths.map(p => p.firstMarker).filter(Boolean)
      );

      for (const s of shapes) {
        const x = (s.x - minX) * scale;
        const y = (s.y - minY) * scale;
        const w = s.width * scale;
        const h = s.height * scale;
        ctx.globalAlpha = 0.8;
        // Match live editor: first marker → orange, special → black, else fill
        if (firstMarkerSet.has(s.id)) ctx.fillStyle = '#ff8c00';
        else if (s.special) ctx.fillStyle = '#000000';
        else ctx.fillStyle = '#' + (s.fill || '003eb3').replace('#', '');
        ctx.fillRect(x, y, w, h);
        ctx.globalAlpha = 1;
      }

      // Draw number/block labels on top of shapes (matches editor ShapeLabels)
      const contrastingTextColor = (hex) => {
        const h = (hex || '003eb3').replace('#', '');
        const r = parseInt(h.substr(0, 2), 16);
        const g = parseInt(h.substr(2, 2), 16);
        const b = parseInt(h.substr(4, 2), 16);
        return (r * 299 + g * 587 + b * 114) / 1000 > 128 ? '#000000' : '#ffffff';
      };
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const s of shapes) {
        if (!s.number && !s.block) continue;
        const x = (s.x - minX) * scale;
        const y = (s.y - minY) * scale;
        const w = s.width * scale;
        const h = s.height * scale;
        const fontPx = Math.max(8, s.width / 2) * scale;
        ctx.font = `bold ${fontPx}px sans-serif`;
        ctx.fillStyle = contrastingTextColor(s.fill);
        // number bottom-right, block top-left — off the centered scan-path line
        if (s.number) ctx.fillText(String(s.number), x + w * 3 / 4, y + h * 3 / 4);
        if (s.block) ctx.fillText(String(s.block), x + w / 4, y + h / 4);
      }

      // Draw scan path lines between shape centers (red, like the live editor)
      if (scanPaths.length) {
        const shapeById = new Map(shapes.map(s => [s.id, s]));
        ctx.globalAlpha = 0.8;
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = Math.max(1, 4 * scale);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (const scanPath of scanPaths) {
          const path = scanPath?.path;
          if (!path || path.length <= 1) continue;
          ctx.beginPath();
          let started = false;
          for (let i = 0; i < path.length; i++) {
            const s = shapeById.get(path[i]);
            if (!s) continue;
            const cx = (s.x + s.width / 2 - minX) * scale;
            const cy = (s.y + s.height / 2 - minY) * scale;
            if (!started) { ctx.moveTo(cx, cy); started = true; }
            else ctx.lineTo(cx, cy);
          }
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
      }

      const blob = await new Promise((resolve, reject) =>
        offscreen.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png')
      );

      // Delete old thumbnail if exists
      if (item?.backgroundImage?.thumbnailBlobId) {
        try { await deleteData(`${URL_IMAGES}/blob/${item.backgroundImage.thumbnailBlobId}`); } catch (_) {}
      }

      // Upload new thumbnail
      const name = `${(item?.backgroundImage?.fileName || id).slice(0, 10)}_thumb`;
      const file = new File([blob], name, { type: 'image/png' });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'thumbnail');
      formData.append('tags', 'editor');
      const postResponse = await postImage(URL_IMAGES, formData);
      const getResponse = await getData(`${URL_IMAGES}/${postResponse._id}`);

      await dispatch(patchLocation({
        locationId: id,
        updateData: {
          ...item,
          backgroundImage: {
            ...item.backgroundImage,
            thumbnailUrl: getResponse.url,
            thumbnailBlobId: getResponse.blobId,
            thumbnailBlobUrl: getResponse.url,
          }
        }
      })).unwrap();

      await dispatch(fetchLocations());
      openNotification('bottomRight', 'Thumbnail aktualisiert', item.name, true);
    } catch (e) {
      console.error(e);
      openNotification('bottomRight', 'Fehler', e?.message || 'Thumbnail konnte nicht generiert werden', false);
    } finally {
      setReloadingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  useEffect(() => {
    dispatch(clearCurrentLocation())
  }, []);

  useEffect(() => {
    if (locationsStatus === 'idle') {
      dispatch(fetchLocations());
    }
  }, [locationsStatus, dispatch]);

  useEffect(() => {
    if (eventStatus === 'idle') {
      dispatch(fetchEvents());
    }
  }, [eventStatus, dispatch]);

  const showModal = () => {
    dispatch(openModal());
  };

  const handleDelete = id => {
    const event = events.find(event => event?.location === id);
    if(event !== undefined){
      openNotification(
        'bottomRight',
        'Fehler',
        'Location in Verwendung und kann nicht gelöscht werden. Bitte Events überprüfen.',
        false
      );
    } else {
      dispatch(deleteLocation(id))
        .then(() => {
          openNotification(
            'bottomRight',
            'Gelöscht',
            'Location wurde erfolgreich gelöscht',
            true
          );
        })
        .catch(() => {
          openNotification(
            'bottomRight',
            'Fehler',
            'Location konnte nicht gelöscht werden',
            false
          );
        });
    }
  };

  if (locationsStatus === 'loading') {
    return (
      <Flex justify="center" align="center" style={{ height: '60vh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (locationsStatus === 'failed') {
    return <div>Fehler: {locationsError?.message ?? locationsError}</div>;
  }

  const showDeleteConfirm = (id) => {
    confirm({
      title: 'Location löschen',
      icon: <ExclamationCircleFilled />,
      content: 'Sind Sie sicher, dass Sie diese Location löschen möchten?',
      okText: 'Ja',
      okType: 'danger',
      cancelText: 'Nein',
      onOk() {
        handleDelete(id)
      },
      onCancel() {
      },
    });
  };

  const copyLocationConfirm = (location) => {
    let newLocationName = ''; // Store the input value

    confirm({
      title: 'Location kopieren',
      icon: <ExclamationCircleFilled />,
      content: (
        <Form layout="vertical">
          <Form.Item
            label="Neuer Standortname"
            name="locationName"
            rules={[{ required: true, message: 'Bitte geben Sie einen neuen Standortnamen ein!' }]}
          >
            <Input
              placeholder="Geben Sie den neuen Standortnamen ein"
              onChange={(e) => (newLocationName = e.target.value)}
            />
          </Form.Item>
        </Form>
      ),
      okText: 'Kopieren',
      okType: 'primary',
      cancelText: 'Abbrechen',
      async onOk() {
        if (!newLocationName.trim()) {
          Modal.error({
            title: 'Fehler',
            content: 'Bitte geben Sie einen gültigen Standortnamen ein!',
          });
          throw new Error('Input validation failed');
        }
        const { createdAt, updatedAt, _id, ...rest } = location;
        const newLocation = {
          ...rest,
          name: newLocationName, // Add the new name
        };
        await dispatch(addLocation(newLocation))
        dispatch(fetchLocations());
        dispatch(resetAddStatus());
      },
      onCancel() {
      },
    });
  };

  return (
    <div style={{ margin: '10px' }}>
      <Button
        type="primary"
        style={{ backgroundColor: 'black', marginBottom: 1 }}
        onClick={showModal}
        icon={<PlusOutlined />}
      >
        {'Location hinzufügen'}
      </Button>
      <Card style={{ height: 60, marginTop: 10, marginBottom: 10 }}>
        <div style={{
          display: 'flex',
          flexFlow: 'row',
          alignItems: 'center',
          marginLeft: -31,
          position: 'relative',
          top: '-10px'
        }}>
          <ConfigProvider theme={{ token: { colorPrimary: '#000' } }}>
            <Flex align="center" gap={8} style={{ marginLeft: 20 }}>
              <Switch
                size="small"
                checked={allExpanded}
                onChange={toggleAll}
              />
              <span style={{ fontSize: 13, color: '#666' }}>Details anzeigen</span>
            </Flex>
          </ConfigProvider>
        </div>
      </Card>

      <List
        grid={{ gutter: 12, xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4 }}
        dataSource={locations}
        renderItem={item => {
          const iconStyle = { fontSize: 16 };
          const hasDetails = item.address || item.description;
          const activeKey = expandedIds.has(item._id) ? ['1'] : [];
          const hasThumbnail = !!item?.backgroundImage?.thumbnailUrl;

          return (
            <List.Item>
              <Card
                styles={{ body: { padding: 0 } }}
                style={{ overflow: 'hidden' }}
              >
                {/* Thumbnail + vertical action bar */}
                <Flex style={{ height: 200 }}>
                  <div style={{ flex: 1, overflow: 'hidden', background: '#fafafa', position: 'relative' }}>
                    <Image
                      alt={item.name}
                      src={item?.backgroundImage?.thumbnailUrl || placeholderSVG}
                      style={{
                        width: '100%',
                        height: '160px',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                      preview={hasThumbnail ? {
                        src: item?.backgroundImage?.fullUrl || item?.backgroundImage?.thumbnailUrl,
                        mask: 'Vergrößern',
                      } : false}
                    />
                    <Tooltip title="Thumbnail aus Layout generieren" placement="bottom">
                        <Button
                          size="small"
                          icon={<ReloadOutlined spin={reloadingIds.has(item._id)} />}
                          loading={reloadingIds.has(item._id)}
                          onClick={() => handleReloadThumbnail(item)}
                          style={{
                            position: 'absolute', bottom: 6, right: 6,
                            background: 'rgba(255,255,255,0.85)',
                            border: '1px solid #ddd',
                            fontSize: 11,
                          }}
                        >
                          Thumbnail generieren
                        </Button>
                      </Tooltip>
                  </div>
                  {/* Vertical icon bar */}
                  <Flex
                    vertical
                    align="center"
                    justify="flex-start"
                    gap={2}
                    style={{
                      width: 40,
                      borderLeft: '1px solid #f0f0f0',
                      background: '#fff',
                      flexShrink: 0,
                      paddingTop: 4,
                    }}
                  >
                    <Tooltip title="Layout bearbeiten" placement="left">
                      <Button
                        type="text"
                        onClick={() => navigate('/locations/editor', { state: { id: item._id } })}
                        icon={<EditOutlined style={iconStyle} />}
                      />
                    </Tooltip>
                    <Tooltip title="Kopieren" placement="left">
                      <Button
                        type="text"
                        onClick={() => copyLocationConfirm(item)}
                        icon={<CopyOutlined style={iconStyle} />}
                      />
                    </Tooltip>
                    <Tooltip title="Löschen" placement="left">
                      <Button
                        type="text"
                        danger
                        onClick={() => showDeleteConfirm(item._id)}
                        icon={<DeleteOutlined style={iconStyle} />}
                      />
                    </Tooltip>
                  </Flex>
                </Flex>

                {/* Name + details */}
                <div style={{ padding: '10px 14px 4px' }}>
                  <Flex align="center" gap={6} style={{ marginBottom: 2 }}>
                    <div style={{ flex: 1, minWidth: 0 }}><FittingTitle name={item.name} /></div>
                    <Tag color="green" style={{ flexShrink: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px', marginRight: 0 }}>
                      {item?.layout?.length || 0} Stühle
                    </Tag>
                  </Flex>
                  <Collapse
                    ghost
                    size="small"
                    activeKey={activeKey}
                    onChange={keys => toggleOne(item._id, keys.length > 0)}
                    items={[{
                      key: '1',
                      label: (
                        <Flex align="center" gap={6}>
                          <span style={{ color: '#aaa', fontSize: 12 }}>Details</span>
                          {hasDetails && (
                            <span style={{
                              width: 6, height: 6, borderRadius: '50%',
                              background: '#1677ff', display: 'inline-block', flexShrink: 0
                            }} />
                          )}
                        </Flex>
                      ),
                      children: (
                        <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                          {hasDetails ? (
                            <>
                              {item.address && <div><strong>Adresse:</strong> {item.address}</div>}
                              {item.description && <div style={{ marginTop: 2 }}><strong>Beschreibung:</strong> {item.description}</div>}
                            </>
                          ) : (
                            <span style={{ color: '#bbb' }}>Keine Details vorhanden</span>
                          )}
                        </div>
                      )
                    }]}
                  />
                </div>
              </Card>
            </List.Item>
          );
        }}
      />
      <AddLocationModal />
    </div>
  );
};

export default Locations;
